import { DayOfWeek } from "@/CAREUI/interactive/WeekdayCheckbox";

import { Badge } from "@/components/ui/badge";

import { Time } from "@/Utils/types";
import { formatName } from "@/Utils/utils";
import type { Code } from "@/types/base/code/code";
import { ChargeItemRead } from "@/types/billing/chargeItem/chargeItem";
import { ChargeItemDefinitionRead } from "@/types/billing/chargeItemDefinition/chargeItemDefinition";
import { EncounterListRead } from "@/types/emr/encounter/encounter";
import {
  PatientListRead,
  PublicPatientRead,
} from "@/types/emr/patient/patient";
import type {
  Priority,
  Status,
} from "@/types/emr/serviceRequest/serviceRequest";
import { TagConfig } from "@/types/emr/tagConfig/tagConfig";
import { FacilityBareMinimum } from "@/types/facility/facility";
import { HealthcareServiceReadSpec } from "@/types/healthcareService/healthcareService";
import { LocationRead } from "@/types/location/location";
import { buildLocationHierarchy } from "@/types/location/utils";
import { TokenRead } from "@/types/tokens/token/token";
import { UserReadMinimal } from "@/types/user/user";

export enum AvailabilitySlotType {
  Appointment = "appointment",
  Open = "open",
  Closed = "closed",
}

export enum SchedulableResourceType {
  Practitioner = "practitioner",
  Location = "location",
  HealthcareService = "healthcare_service",
}

export enum AvailabilityRecurrenceType {
  Weekly = "weekly",
  Monthly = "monthly",
}

export const SCHEDULABLE_RESOURCE_TYPE_COLORS = {
  practitioner: "blue",
  location: "green",
  healthcare_service: "yellow",
} as const satisfies Record<SchedulableResourceType, string>;

export interface AvailabilityDateTime {
  recurrence?: AvailabilityRecurrenceType;
  day_of_week?: DayOfWeek;
  day_of_month?: number;
  start_time: Time;
  end_time: Time;
}

export interface ScheduleTemplate {
  id: string;
  name: string;
  valid_from: string;
  valid_to: string;
  availabilities: ScheduleAvailability[];
  created_by: UserReadMinimal;
  updated_by: UserReadMinimal;
  charge_item_definition: ChargeItemDefinitionRead;
  revisit_charge_item_definition: ChargeItemDefinitionRead;
  revisit_allowed_days: number;
  is_public: boolean;
}

type ScheduleAvailabilityBase = {
  name: string;
  reason: string;
  availability: AvailabilityDateTime[];
} & (
  | {
      slot_type: AvailabilitySlotType.Appointment;
      slot_size_in_minutes: number;
      tokens_per_slot: number;
    }
  | {
      slot_type: AvailabilitySlotType.Open | AvailabilitySlotType.Closed;
      slot_size_in_minutes: null;
      tokens_per_slot: null;
    }
);

export interface ScheduleTemplateCreateRequest {
  name: string;
  valid_from: string; // datetime
  valid_to: string; // datetime
  availabilities: ScheduleAvailabilityBase[];
  resource_type: SchedulableResourceType;
  resource_id: string;
  is_public: boolean;
}
export interface ScheduleTemplateSetChargeItemDefinitionRequest {
  charge_item_definition: string;
  re_visit_allowed_days: number;
  re_visit_charge_item_definition: string | null;
}
export interface ScheduleTemplateUpdateRequest {
  name: string;
  valid_from: string;
  valid_to: string;
  is_public: boolean;
}

export type ScheduleAvailability = ScheduleAvailabilityBase & {
  id: string;
};

export type ScheduleAvailabilityCreateRequest = ScheduleAvailabilityBase;

export interface ScheduleException {
  id: string;
  reason: string;
  valid_from: string; // date in YYYY-MM-DD format
  valid_to: string; // date in YYYY-MM-DD format
  start_time: Time;
  end_time: Time;
}

export interface ScheduleExceptionCreateRequest {
  resource_type: SchedulableResourceType;
  resource_id: string;
  reason: string;
  valid_from: string;
  valid_to: string;
  start_time: Time;
  end_time: Time;
}

export interface TokenSlot {
  id: string;
  availability: {
    name: string;
    tokens_per_slot: number;
    charge_item_definition: ChargeItemDefinitionRead | null;
  };
  start_datetime: string; // timezone naive datetime
  end_datetime: string; // timezone naive datetime
  allocated: number;
}

export interface GetSlotsForDayResponse {
  results: TokenSlot[];
}

export interface AvailabilityHeatmapRequest {
  from_date: string;
  to_date: string;
  resource_type: SchedulableResourceType;
  resource_id: string;
}

export interface AvailabilityHeatmapDay {
  total_slots: number;
  booked_slots: number;
  total_time_slots: number;
  available_time_slots: number;
}

export interface AvailabilityHeatmapResponse {
  [date: string]: AvailabilityHeatmapDay;
}

export enum AppointmentStatus {
  PROPOSED = "proposed",
  PENDING = "pending",
  BOOKED = "booked",
  ARRIVED = "arrived",
  CHECKED_IN = "checked_in",
  WAITLIST = "waitlist",
  IN_CONSULTATION = "in_consultation",
  FULFILLED = "fulfilled",
  NO_SHOW = "noshow",
  CANCELLED = "cancelled",
  ENTERED_IN_ERROR = "entered_in_error",
  RESCHEDULED = "rescheduled",
}

export enum AppointmentVisitType {
  OPD = "opd",
  IPD = "ipd",
  ER = "er",
  TELECONSULTATION = "teleconsultation",
  HOME_VISIT = "home_visit",
}

export enum PatientVisitType {
  ROUTINE = "routine",
  PRIORITY = "priority",
}

export interface PaymentGateStatus {
  applicable: boolean;
  blocked: boolean;
  paid: boolean;
  visit_type: AppointmentVisitType | null;
  source: string | null;
  reason: string | null;
}

export const APPOINTMENT_VISIT_TYPE_LABELS: Record<
  AppointmentVisitType,
  string
> = {
  [AppointmentVisitType.OPD]: "OPD",
  [AppointmentVisitType.IPD]: "IPD",
  [AppointmentVisitType.ER]: "ER",
  [AppointmentVisitType.TELECONSULTATION]: "Teleconsultation",
  [AppointmentVisitType.HOME_VISIT]: "Home Visit",
};

export const PATIENT_VISIT_TYPE_LABELS: Record<PatientVisitType, string> = {
  [PatientVisitType.ROUTINE]: "Routine Visit",
  [PatientVisitType.PRIORITY]: "Priority Visit",
};

export const UpcomingAppointmentStatuses = [
  AppointmentStatus.PROPOSED,
  AppointmentStatus.PENDING,
  AppointmentStatus.BOOKED,
  AppointmentStatus.ARRIVED,
  AppointmentStatus.CHECKED_IN,
  AppointmentStatus.WAITLIST,
  AppointmentStatus.IN_CONSULTATION,
];

export const TodayAppointmentStatuses = [
  AppointmentStatus.PROPOSED,
  AppointmentStatus.PENDING,
  AppointmentStatus.BOOKED,
  AppointmentStatus.ARRIVED,
  AppointmentStatus.CHECKED_IN,
  AppointmentStatus.WAITLIST,
  AppointmentStatus.IN_CONSULTATION,
  AppointmentStatus.FULFILLED,
  AppointmentStatus.NO_SHOW,
  AppointmentStatus.CANCELLED,
  AppointmentStatus.ENTERED_IN_ERROR,
  AppointmentStatus.RESCHEDULED,
];

export const PastAppointmentStatuses = [
  ...UpcomingAppointmentStatuses,
  AppointmentStatus.FULFILLED,
  AppointmentStatus.NO_SHOW,
];

export const CancelledAppointmentStatuses = [
  AppointmentStatus.CANCELLED,
  AppointmentStatus.ENTERED_IN_ERROR,
  AppointmentStatus.RESCHEDULED,
  AppointmentStatus.NO_SHOW,
];

export const AppointmentFinalStatuses = [
  ...CancelledAppointmentStatuses,
  AppointmentStatus.FULFILLED,
  AppointmentStatus.NO_SHOW,
];

export const APPOINTMENT_STATUS_COLORS = {
  proposed: "secondary",
  pending: "secondary",
  booked: "blue",
  arrived: "primary",
  fulfilled: "primary",
  noshow: "orange",
  checked_in: "green",
  waitlist: "secondary",
  in_consultation: "primary",
  cancelled: "destructive",
  entered_in_error: "destructive",
  rescheduled: "yellow",
} as const satisfies Record<
  AppointmentStatus,
  React.ComponentProps<typeof Badge>["variant"]
>;

type LocationResource = {
  resource: LocationRead;
  resource_type: SchedulableResourceType.Location;
};

type UserResource = {
  resource: UserReadMinimal;
  resource_type: SchedulableResourceType.Practitioner;
};

type HealthcareServiceResource = {
  resource: HealthcareServiceReadSpec;
  resource_type: SchedulableResourceType.HealthcareService;
};

export type ScheduleResource =
  | UserResource
  | LocationResource
  | HealthcareServiceResource;

export type AppointmentBase = {
  id: string;
  token_slot: TokenSlot;
  booked_on: string;
  status: AppointmentStatus;
  visit_type: AppointmentVisitType | null;
  patient_visit_type: PatientVisitType;
  note: string;
  facility: FacilityBareMinimum;
  token: TokenRead | null;
  booked_by: UserReadMinimal | null; // This is null if the appointment was booked by the patient itself.
  charge_item: ChargeItemRead | null;
  payment_gate?: PaymentGateStatus;
  pending_expiry: string | null;
} & ScheduleResource;

export type Appointment = AppointmentBase & {
  patient: PatientListRead;
};

export interface PrioritySlotAlertCandidate {
  id: string;
  patient: {
    id: string;
    name: string;
    phone_number: string;
  };
  current_slot: {
    id: string;
    start_datetime: string;
    end_datetime: string;
  };
}

export interface PrioritySlotAlert {
  opened_slot: {
    id: string;
    start_datetime: string;
    end_datetime: string;
  };
  candidates: PrioritySlotAlertCandidate[];
}

export type AppointmentActionResponse = Appointment & {
  priority_slot_alert?: PrioritySlotAlert;
};

export type PublicAppointment = AppointmentBase & {
  patient: PublicPatientRead | null;
};

export interface AppointmentServiceRequestRead {
  id: string;
  title: string;
  status: Status;
  priority: Priority;
  category: string;
  code?: Code | null;
}

export type AppointmentRead = Appointment & {
  tags: TagConfig[];
  updated_by: UserReadMinimal | null;
  created_by: UserReadMinimal;
  modified_date: string;
  associated_encounter?: EncounterListRead;
  service_request?: AppointmentServiceRequestRead | null;
  service_request_required?: boolean;
};

export interface AppointmentCreateRequest {
  patient: string;
  note: string;
  tags: string[];
  visit_type?: AppointmentVisitType | null;
  patient_visit_type?: PatientVisitType;
  encounter?: string;
  service_request?: string | null;
}

export interface AppointmentCreatePublicRequest {
  patient: string;
  note: string;
  patient_visit_type?: PatientVisitType;
}

export interface AppointmentReservePublicRequest {
  note: string;
  patient_visit_type?: PatientVisitType;
}

export interface AppointmentUpdateRequest {
  status: Appointment["status"];
  note: string;
  encounter?: string | null;
  service_request?: string | null;
}

export interface AppointmentLinkServiceRequest {
  service_request: string;
}

export interface CreateAppointmentQuestion {
  note: string;
  slot_id: string;
  tags: string[];
  visit_type?: AppointmentVisitType | null;
  patient_visit_type?: PatientVisitType;
}

export const getAppointmentPaymentBlocked = (appointment: AppointmentRead) =>
  appointment.payment_gate?.blocked ?? false;

export interface AppointmentCancelRequest {
  reason: "cancelled" | "entered_in_error";
  note?: string;
}

export interface AppointmentRescheduleRequest {
  new_slot: string;
  previous_booking_note: string;
  new_booking_note: string;
  tags: string[];
}

export interface PrioritySlotNotificationRequest {
  candidate_booking: string;
  opened_slot: string;
}

export interface PrioritySlotNotificationResponse {
  sent: boolean;
  sms_disabled: boolean;
  message: string | null;
}

export const getUserFromLocalStorage = (): UserReadMinimal => {
  return JSON.parse(localStorage.getItem("user") ?? "{}");
};

export const storeUserInLocalStorage = (user: UserReadMinimal) => {
  localStorage.setItem("user", JSON.stringify(user));
};

export const formatScheduleResourceName = (appointment: ScheduleResource) => {
  switch (appointment.resource_type) {
    case SchedulableResourceType.Practitioner:
      return formatName(appointment.resource);
    case SchedulableResourceType.Location:
      return buildLocationHierarchy(appointment.resource).join(" > ");
    case SchedulableResourceType.HealthcareService:
      return appointment.resource.name;
    default:
      return "-";
  }
};
